import {
  ArrowRight,
  BedDouble,
  Palmtree,
  Plane,
  Car,
  House,
  Ship,
  Sparkles,
  Mountain,
  Route,
  Bus,
  Compass,
  Luggage,
  Coins,
  Star,
  Ticket,
  Users,
  ShieldCheck,
  MessageCircle,
} from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { LINKS, IMAGES } from "@/data/content";

const CATEGORIES = [
  { icon: BedDouble, label: "Hotel" },
  { icon: Palmtree, label: "Resort" },
  { icon: Plane, label: "Voli" },
  { icon: Car, label: "Noleggio auto" },
  { icon: House, label: "Ville e case vacanza" },
  { icon: Ship, label: "Crociere" },
  { icon: Sparkles, label: "Attività" },
  { icon: Mountain, label: "Escursioni" },
  { icon: Route, label: "Transfer" },
  { icon: Bus, label: "Ground Transport" },
  { icon: Compass, label: "Life Experiences" },
  { icon: Luggage, label: "Getaways" },
];

const BENEFITS = [
  {
    icon: Coins,
    title: "Travel Credits",
    text: "Prenoti, accumuli Travel Credits e puoi utilizzarli per i tuoi viaggi successivi. Semplice così.",
  },
  {
    icon: Star,
    title: "Loyalty Points",
    text: "La membership include anche punti fedeltà, utilizzabili secondo le condizioni previste dalla piattaforma.",
  },
  {
    icon: Compass,
    title: "Life Experiences",
    text: "Non solo prenotare: anche vivere esperienze, attività e momenti che rendono un viaggio indimenticabile.",
  },
  {
    icon: Ticket,
    title: "Guest Pass",
    text: "Esiste anche la possibilità di far conoscere e provare la piattaforma ad altre persone, secondo le condizioni previste.",
  },
];

export default function TravelAdvantage() {
  return (
    <div data-testid="travel-advantage-page">
      <Seo
        title="TravelAdvantage — Il club dei viaggiatori | Laure Hila"
        description="TravelAdvantage è un club di viaggiatori: hotel, resort, voli, crociere, ville, esperienze e molto di più. Te lo mostro come funziona, senza promesse e senza pressioni."
      />

      <section className="pt-32 md:pt-44 pb-14 md:pb-20 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="eyebrow mb-5">Il club</p>
          </FadeIn>
          <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
              TravelAdvantage —
            </MaskedLine>
            <MaskedLine delay={0.25} className="text-5xl md:text-7xl italic text-gold">
              il club dei viaggiatori
            </MaskedLine>
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-stone">
              TravelAdvantage è un club pensato per chi ama viaggiare. Io la uso e
              la racconto per quello che è: uno strumento, non una promessa. E
              c'è molto più di un semplice sito per prenotare un hotel.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-ink mb-10 md:mb-12">
              Cosa puoi <span className="italic text-gold">prenotare</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.04}>
                <div
                  data-testid={`ta-category-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="h-full p-5 md:p-6 rounded-xl bg-sand/70 border border-line hover:border-gold transition-colors duration-500 group flex items-center gap-4"
                >
                  <c.icon
                    size={26}
                    className="text-gold flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-serif text-lg md:text-xl text-ink leading-tight">{c.label}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden rounded-xl">
              <img
                src={IMAGES.valigia.url}
                alt={IMAGES.valigia.alt}
                className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-ink mb-8">
                I vantaggi del <span className="italic text-gold">club</span>
              </h2>
            </Reveal>
            <div className="flex flex-col gap-6">
              {BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.07}>
                  <div
                    data-testid={`ta-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex gap-5 items-start"
                  >
                    <span className="w-11 h-11 rounded-full bg-sand border border-line flex items-center justify-center flex-shrink-0">
                      <b.icon size={20} className="text-gold" strokeWidth={1.5} />
                    </span>
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl text-ink">{b.title}</h3>
                      <p className="mt-1.5 text-sm md:text-base leading-relaxed text-stone">{b.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-2xl bg-ink text-cream grain relative p-10 md:p-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                <div>
                  <Users size={28} className="text-gold" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif text-2xl">Un club, non una vendita</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">
                    Ne parlo perché lo uso e mi piace. Nessuna pressione, nessun
                    obbligo: solo informazioni chiare.
                  </p>
                </div>
                <div>
                  <Coins size={28} className="text-gold" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif text-2xl">Trasparenza prima di tutto</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">
                    Costi, condizioni e funzionamento sono sempre visibili sulla
                    piattaforma ufficiale prima di qualsiasi scelta.
                  </p>
                </div>
                <div>
                  <ShieldCheck size={28} className="text-gold" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif text-2xl">Ai tuoi tempi</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">
                    Se vuoi capire se fa per te, ne parliamo insieme con calma. La
                    decisione è sempre e solo tua.
                  </p>
                </div>
              </div>
              <div className="mt-12 pt-10 border-t border-cream/10">
                <p className="font-serif text-2xl md:text-3xl text-balance">
                  Vuoi capire come funziona?{" "}
                  <span className="italic text-gold">Se vuoi, posso mostrarti la piattaforma e spiegarti come la utilizzo.</span>
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a
                    href={LINKS.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ta-cta-scopri"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gold text-ink font-semibold text-sm tracking-wide hover:bg-cream transition-colors duration-300"
                  >
                    <MessageCircle size={16} />
                    Scopri come funziona
                  </a>
                  <a
                    href={LINKS.travelAdvantage}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ta-cta-guarda"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-cream/30 text-cream font-semibold text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300 group"
                  >
                    Guarda Travel Advantage
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
